import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { LuArrowLeft, LuCalendar, LuClock, LuUser, LuArrowRight } from 'react-icons/lu';
import { blogs } from '../data/blogs';

export default function BlogPost() {
  const { slug } = useParams();
  const currentIndex = blogs.findIndex(b => b.slug === slug);
  const blog = blogs[currentIndex];
  
  const previousBlog = currentIndex > 0 ? blogs[currentIndex - 1] : null;
  const nextBlog = currentIndex < blogs.length - 1 ? blogs[currentIndex + 1] : null;

  if (!blog) {
    return (
      <div className="bg-slate-950 min-h-screen pt-32 px-6">
        <div className="container mx-auto text-center">
          <h1 className="text-4xl font-bold text-white">Blog post not found</h1>
          <Link to="/blog" className="text-orange-400 hover:text-orange-300 mt-4 inline-block">Back to Blog</Link>
        </div>
      </div>
    );
  }

  const renderContent = (content) => {
    return content.split('\n').map((line, idx) => {
      const trimmed = line.trim();
      if (!trimmed) return <br key={idx} />;
      
      if (trimmed.startsWith('## ')) {
        return (
          <h2 key={idx} className="text-3xl font-heading font-bold mt-12 mb-6 bg-gradient-to-r from-blue-400 to-orange-400 bg-clip-text text-transparent">
            {trimmed.replace('## ', '')}
          </h2>
        );
      }
      if (trimmed.startsWith('### ')) {
        return <h3 key={idx} className="text-2xl font-heading font-bold mt-8 mb-4 text-white">{trimmed.replace('### ', '')}</h3>;
      }
      if (trimmed.startsWith('- ')) {
        return (
          <li key={idx} className="text-slate-300 ml-6 mb-2 list-disc">
            {trimmed.replace('- ', '')}
          </li>
        );
      }
      if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
        return <p key={idx} className="text-white font-bold mb-4">{trimmed.replace(/\*\*/g, '')}</p>;
      }
      
      return <p key={idx} className="text-slate-300 leading-relaxed mb-4">{trimmed}</p>;
    });
  };

  return (
    <div className="bg-slate-950" data-testid="blog-post-page">
      <section className="pt-32 pb-12">
        <div className="container mx-auto px-6 md:px-8">
          <Link to="/blog" className="inline-flex items-center space-x-2 text-orange-400 hover:text-orange-300 transition-colors mb-8 group">
            <LuArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Blog</span>
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <span className="inline-block px-4 py-2 rounded-full bg-gradient-to-r from-blue-600/20 to-orange-600/20 border border-blue-500/30 text-blue-400 text-sm font-semibold mb-6">
              {blog.category}
            </span>

            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              {blog.title}
            </h1>

            <div className="flex flex-wrap items-center gap-6 text-slate-400 mb-8">
              <div className="flex items-center space-x-2">
                <LuUser size={18} />
                <span>{blog.author}</span>
              </div>
              <div className="flex items-center space-x-2">
                <LuCalendar size={18} />
                <span>{blog.date}</span>
              </div>
              <div className="flex items-center space-x-2">
                <LuClock size={18} />
                <span>{blog.readTime}</span>
              </div>
            </div>

            <div className="aspect-video rounded-3xl overflow-hidden mb-12 border border-slate-800">
              <img
                src={blog.image}
                alt={blog.title}
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </section>

      <section className="pb-12">
        <div className="container mx-auto px-6 md:px-8">
          <motion.article
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="prose prose-lg max-w-none">
              {renderContent(blog.content)}
            </div>
          </motion.article>
        </div>
      </section>

      <section className="py-12 border-t border-slate-800">
        <div className="container mx-auto px-6 md:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {previousBlog ? (
                <Link
                  to={`/blog/${previousBlog.slug}`}
                  className="group bg-slate-900/50 border border-slate-800 hover:border-blue-500/50 rounded-2xl p-6 transition-all"
                >
                  <div className="flex items-center space-x-2 text-blue-400 mb-3">
                    <LuArrowLeft size={20} />
                    <span className="text-sm font-semibold">Previous Post</span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white group-hover:text-blue-400 transition-colors line-clamp-2">
                    {previousBlog.title}
                  </h3>
                </Link>
              ) : (
                <div />
              )}
              
              {nextBlog && (
                <Link
                  to={`/blog/${nextBlog.slug}`}
                  className="group bg-slate-900/50 border border-slate-800 hover:border-orange-500/50 rounded-2xl p-6 transition-all text-right"
                >
                  <div className="flex items-center justify-end space-x-2 text-orange-400 mb-3">
                    <span className="text-sm font-semibold">Next Post</span>
                    <LuArrowRight size={20} />
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white group-hover:text-orange-400 transition-colors line-clamp-2">
                    {nextBlog.title}
                  </h3>
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
